import { useEffect, useMemo, useState } from 'react';
import { penly } from 'api';
import { legalProducts, financialProducts } from 'utils/constants';
import { config } from 'utils/config';
import { getAffiliateProductOptions, getProductOptionGroups } from '../services/ordersTable';

const legalProductIds = new Set(legalProducts.map((product) => product.value));

export const useAccessProductOptions = (organisationId, showLegal = false) => {
    const [productOptions, setProductOptions] = useState([]);
    const [productsById, setProductsById] = useState({});
    const showAll = config.env === 'test';

    const fallbackProductOptions = useMemo(() => (showAll
        ? [...financialProducts, ...legalProducts]
        : (showLegal ? legalProducts : financialProducts))
        .map((product) => ({ ...product, isLaw: legalProductIds.has(product.value) })), [showAll, showLegal]);

    useEffect(() => {
        let active = true;

        const fetchProductOptions = async () => {
            try {
                const affiliateIds = showAll
                    ? ['penly', 'jura-nord-advokater-aps']
                    : (showLegal ? ['jura-nord-advokater-aps'] : ['penly']);

                const affiliates = await Promise.all(
                    affiliateIds.map((affiliateId) => penly.getAffiliate(organisationId, affiliateId, { details: true })),
                );

                const fetchedOptions = getAffiliateProductOptions(affiliates, showLegal);
                const mappedProductsById = affiliates.reduce((acc, affiliate) => {
                    const isLawAffiliate = affiliate?.id === 'jura-nord-advokater-aps';
                    Object.values(affiliate?.products || {}).forEach((product) => {
                        if (!product || typeof product !== 'object') return;
                        const isHidden = 'hidden' in product ? Boolean(product.hidden) : false;
                        const productId = 'id' in product ? product.id : undefined;
                        const productName = 'name' in product ? product.name : undefined;
                        if (typeof productId !== 'string' || typeof productName !== 'string' || isHidden) return;
                        acc[productId] = { ...product, isLaw: isLawAffiliate };
                    });
                    return acc;
                }, {});

                if (active) {
                    setProductOptions(fetchedOptions.length ? fetchedOptions : fallbackProductOptions);
                    setProductsById(mappedProductsById);
                }
            } catch {
                if (active) {
                    setProductOptions(fallbackProductOptions);
                    setProductsById({});
                }
            }
        };

        fetchProductOptions();

        return () => {
            active = false;
        };
    }, [organisationId, showAll, showLegal, fallbackProductOptions]);

    const { lawProductOptions, nonLawProductOptions } = useMemo(
        () => getProductOptionGroups(productOptions),
        [productOptions],
    );

    return { productOptions, lawProductOptions, nonLawProductOptions, productsById };
};
